import { createFileRoute, Link } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";

import { Loader2, Upload, CreditCard } from "lucide-react";
import { getServiceBadge } from "@/lib/service-visuals";
import { useTranslation } from "react-i18next";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";

import { Button } from "@/components/ui/button";
import { fetchServices } from "@/lib/content";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { breadcrumbScript } from "@/lib/breadcrumb-jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";
import { jsonLdScript } from "@/lib/jsonld";

const servicesQueryOptions = queryOptions({
  queryKey: ["services"],
  queryFn: () => fetchServices(),
  staleTime: 5 * 60 * 1000,
});

const SERVICE_SCHEMAS: Array<{ name: string; description: string; slug: string }> = [
  {
    name: "Income Tax Return Filing",
    slug: "income-tax-return-filing",
    description: "Online income tax return filing in Bangladesh by an NBR-certified tax consultant, covering individual and company return preparation, salary tax computation, e-TIN based return submission and assessment support.",
  },
  {
    name: "VAT Registration & Compliance",
    slug: "vat-registration-compliance",
    description: "End-to-end VAT registration (BIN) and ongoing VAT compliance in Bangladesh, including Mushak 9.1 monthly VAT return filing, VAT arrear return, VAT consultancy and audit support.",
  },
  {
    name: "TIN Certificate Assistance",
    slug: "tin-certificate-assistance",
    description: "e-TIN certificate registration, recovery and correction service for individuals and businesses in Bangladesh, including support for salary, bank account, trade license and compliance documentation.",
  },
];

function serviceSchemaScripts() {
  return SERVICE_SCHEMAS.map((s) =>
    jsonLdScript({
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `https://onlinetaxvat.com/services#${s.slug}`,
      name: s.name,
      serviceType: s.name,
      description: s.description,
      provider: { "@id": "https://onlinetaxvat.com/#organization" },
      areaServed: { "@type": "Country", name: "Bangladesh" },
      availableLanguage: ["en", "bn"],
      url: "https://onlinetaxvat.com/services",
      category: "Tax & VAT Consultancy",
    }),
  );
}

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Income Tax Return Form Filing & Tax Payment Services Bangladesh | Online Tax VAT" },
      { name: "description", content: "File your income tax return form online in Bangladesh and complete NBR tax payment with certified consultants — plus VAT registration (BIN), Mushak 9.1, RJSC company setup and trade license services." },
      { property: "og:title", content: "Income Tax Return Form, Tax Payment & VAT Services | Online Tax VAT Bangladesh" },
      { property: "og:description", content: "Complete income tax return form preparation, online NBR tax payment, VAT registration (BIN), Mushak 9.1, RJSC company registration, ERC/IRC and trade license services for Bangladesh." },
      { property: "og:url", content: "https://onlinetaxvat.com/services" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Income Tax Return Form & Tax Payment Services | Online Tax VAT Bangladesh" },
      { name: "twitter:description", content: "Income tax return form filing, NBR tax payment, online VAT registration BD, RJSC company registration and trade license — fully online." },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/services" }],
    scripts: [breadcrumbScript([
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
    ]), webPageScript({
      path: "/services",
      name: "Services — Online Tax VAT Bangladesh",
      description: "Income tax return filing, online VAT registration BD, RJSC company registration, ERC/IRC and trade license online bangladesh.",
    }), ...serviceSchemaScripts()],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(servicesQueryOptions),
  component: ServicesPage,
  pendingComponent: () => (
    <SiteShell>
      <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
    </SiteShell>
  ),
});


function ServicesPage() {
  const { t, i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
  const { data: services } = useSuspenseQuery(servicesQueryOptions);
  const loading = false;

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-16">
        <div className="container-page">
          <h1 className="sr-only">Income Tax Return Form Filing, Tax Payment &amp; VAT Services in Bangladesh</h1>
          <p className="sr-only">Professional income tax return form preparation, secure online NBR tax payment, VAT registration (BIN) and Mushak 9.1 return filing for individuals and businesses across Bangladesh — delivered by NBR-certified tax consultants.</p>
          <SectionHeading
            eyebrow={t("services.eyebrow")}
            eyebrowClassName="!text-white !bg-primary/30 !border-primary/40"
            title={<>{t("services.titleA")}<span className="gradient-text">{t("services.titleB")}</span></>}
            description={t("services.desc")}
          />
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
            <Link to="/services/$slug" params={{ slug: "income-tax-return" }} className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors">Income Tax Return →</Link>
            <Link to="/services/$slug" params={{ slug: "vat-registration" }} className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors">VAT Registration →</Link>
            <Link to="/services/$slug" params={{ slug: "tin-registration" }} className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors">TIN Registration →</Link>
            <Link to="/services/$slug" params={{ slug: "rjsc-company-registration" }} className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors">Company Tax / RJSC →</Link>
            <Link to="/services/$slug" params={{ slug: "trade-license" }} className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 px-4 py-1.5 text-xs font-semibold hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors">Trade License →</Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="mt-12 grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {services.map((s) => (
                <div key={s.id}>
                  <div className="group relative h-full rounded-2xl p-3 sm:p-6 flex flex-col bg-[#0f5132] border border-[#0f5132] shadow-[0_10px_30px_-12px_rgba(15,81,50,0.55)] hover:shadow-[0_20px_45px_-15px_rgba(15,81,50,0.75)] hover:-translate-y-1 transition-all duration-150">
                    <Link to="/packages" search={{ service: s.slug } as any} className="block" preload="intent">
                      {(() => {
                        const { Icon: BadgeIcon, colorClass } = getServiceBadge(s.slug, s.title, s.title_bn);
                        return (
                          <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full ${colorClass} text-white flex items-center justify-center shadow-md ring-2 ring-white/20 group-hover:scale-105 transition-transform duration-150`}>
                            <BadgeIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                          </div>
                        );
                      })()}
                      <h3 className="mt-2 sm:mt-4 font-display font-extrabold text-sm sm:text-lg text-white">{isBn && s.title_bn ? s.title_bn : s.title}</h3>
                      <p className="mt-1 sm:mt-1.5 text-xs sm:text-sm text-emerald-100/80 line-clamp-2 sm:line-clamp-3">{isBn && s.description_bn ? s.description_bn : s.description}</p>
                    </Link>
                    <div className="mt-auto pt-2 sm:pt-4 mt-3 sm:mt-5 border-t border-emerald-400/25 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-1.5 sm:gap-2">
                      <Link to="/upload" search={{ service: s.slug } as any} preload="intent"
                        className="inline-flex items-center justify-center rounded-md bg-emerald-400 text-emerald-950 px-2 sm:px-3 py-1 text-xs font-bold hover:bg-emerald-300 transition-colors duration-150">
                        <Upload className="h-3 w-3 mr-1" />
                        {t("home.servicesSection.uploadDocs", { defaultValue: "Upload Documents" })}
                      </Link>
                      <Link to="/packages" search={{ service: s.slug } as any} preload="intent"
                        className="inline-flex items-center justify-center rounded-md bg-white text-[#0f5132] px-2 sm:px-3 py-1 text-xs font-bold hover:bg-emerald-50 transition-colors duration-150">
                        <CreditCard className="h-3 w-3 mr-1" />
                        {t("services.choosePackage")}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-14 text-center">
            <p className="text-sm text-muted-foreground mb-3">{t("services.ctaQuestion")}</p>
            <Button asChild size="lg" className="gradient-primary text-primary-foreground shadow-glow">
              <Link to="/contact">{t("services.ctaButton")}</Link>
            </Button>
          </div>
          <section className="mt-16 max-w-4xl mx-auto text-left">
            {isBn ? (
              <>
                <h1 className="font-display text-3xl font-bold mb-4">কোম্পানি গঠন ও কর্পোরেট কমপ্লায়েন্স সহায়তা</h1>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed font-medium mb-4">
                  বাংলাদেশে আরজেএসসি (RJSC) কোম্পানি রেজিস্ট্রেশন, নেম ক্লিয়ারেন্স, লিমিটেড কোম্পানি গঠন, শেয়ার ট্রান্সফার এবং বার্ষিক রিটার্ন দাখিল-সহ কর্পোরেট লাইফসাইকেলের সম্পূর্ণ আইনি প্রসেস আমরা পরিচালনা করি।
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  নতুন ব্যবসার জন্য আমাদের টিম অনলাইন ট্রেড লাইসেন্স ইস্যু, পার্টনারশিপ ডিড (অংশীদারিত্ব চুক্তি) এবং প্রোপরাইটরশিপ ব্যবসার ট্রেড লাইসেন্স সেটআপের কাজ অত্যন্ত দ্রুততার সাথে সম্পন্ন করে থাকে।
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mt-3">
                  আমদানিকারক ও রপ্তানিকারকদের জন্য আমরা ইআরসি (ERC) ও আইআরসি (IRC) রেজিস্ট্রেশন, বিডা (BIDA) ইনভেস্টমেন্ট রেজিস্ট্রেশন এবং চেম্বার অব কমার্স মেম্বারশিপের আবেদন ফাইল করি। এছাড়াও আমরা ফায়ার লাইসেন্স, ফ্যাক্টরি লাইসেন্স, পরিবেশগত ছাড়পত্র (Environment Clearance), BSCI ও ISO সার্টিফিকেশন, ট্রেডমার্ক, পেটেন্ট এবং কপিরাইট রেজিস্ট্রেশন সেবায় সম্পূর্ণ আইনি ও লজিস্টিক সাপোর্ট প্রদান করি।
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mt-3">
                  ট্যাক্সেশনের ক্ষেত্রে আমরা নতুন আয়কর আইন ও এনবিআর (NBR)-এর সর্বশেষ সার্কুলার অনুযায়ী কর্পোরেট ট্যাক্স রিটার্ন দাখিল, ব্যক্তিগত আয়কর রিটার্ন সাবমিশন, ই-টিন (e-TIN) রেজিস্ট্রেশন, ট্যাক্স অডিট, কর রেয়াত এবং অ্যাসেসমেন্ট বা কর নির্ধারণী শুনানির কাজ নিখুঁতভাবে সম্পন্ন করি।
                </p>
              </>
            ) : (
              <>
                <h2 className="font-display text-2xl md:text-3xl font-extrabold mb-4">
                  Income tax return form submission, fast tax payment & corporate compliance in Bangladesh
                </h2>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                  Beyond preparing your income tax return form and handling NBR tax payment, we manage the full corporate lifecycle: <strong>RJSC company registration bangladesh</strong>, name clearance, <strong>limited company formation dhaka</strong>, share transfer and annual return filing RJSC. Our team also issues <strong>trade license online bangladesh</strong>, partnership deeds and sole proprietorship trade license setups for new ventures across Dhaka.
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4">
                  For importers and exporters we file <strong>ERC registration bangladesh</strong>, IRC registration, import &amp; export registration certificates, BIDA investment registration and chamber of commerce membership. We also support fire license, factory license, environment clearance, BSCI &amp; ISO certification, trademark, patent and copyright registration in Bangladesh.
                </p>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  On the tax side we deliver <strong>corporate tax return filing</strong>, individual income tax return Bangladesh, e-TIN registration, tax audit, tax rebate and assessment support — guided by the new income tax act Bangladesh and the latest NBR circulars.
                </p>
              </>
            )}
          </section>
          {(() => {
            const c = SEO_CONTENT["services"];
            const bn = {
              title: "বাংলাদেশি করদাতাদের জন্য সমন্বিত ট্যাক্স ও ভ্যাট সেবা",
              intro:
                "প্রথমবার আয়কর রিটার্ন দাখিল করা চাকরিজীবী পেশাদার থেকে শুরু করে প্রতি মাসের ভ্যাট ও রিটার্ন ম্যানেজ করা লিমিটেড কোম্পানি — আমাদের এনবিআর-প্রত্যয়িত টিম সবাইকে এন্ড-টু-এন্ড কমপ্লায়েন্স সেবা দেয়। প্রতিটি সেবায় ডকুমেন্ট রিভিউ, ই-রিটার্ন সাবমিশন, অফিশিয়াল প্রাপ্তি স্বীকারপত্র (Acknowledgement Slip) এবং পরবর্তী সাপোর্ট অন্তর্ভুক্ত থাকে।",
              bullets: [
                "ব্যক্তিগত আয়কর রিটার্ন (IT-11GA): চাকরিজীবী, ফ্রিল্যান্সার এবং এসএমই (SME) উদ্যোক্তাদের জন্য ট্যাক্স রিটার্ন দাখিল",
                "ভ্যাট নিবন্ধন ও মাসিক রিটার্ন: মূসক ২.১ ভ্যাট রেজিস্ট্রেশন এবং প্রতি মাসের মূসক ৯.১ রিটার্ন সাবমিশন",
                "ই-টিন (e-TIN) সেবা: নতুন ই-টিন ইস্যু, তথ্য সংশোধন এবং রি-রেজিস্ট্রেশন সহায়তা",
                "বিন (BIN) সার্টিফিকেট: নতুন ব্যবসা ও লিমিটেড কোম্পানির জন্য বিজনেস আইডেন্টিফিকেশন নাম্বার লাইসেন্স",
                "ট্রেড লাইসেন্স প্রসেসিং: ঢাকা (DSCC, DNCC), চট্টগ্রাম-সহ দেশের যেকোনো প্রান্তের নতুন ট্রেড লাইসেন্স ও নবায়ন",
                "কর্পোরেট ট্যাক্স ও অডিট: কোম্পানির ট্যাক্স ফাইলিং, অডিট রিপোর্ট প্রস্তুত এবং ফাইনাল অ্যাসেসমেন্ট ডিফেন্স",
              ],
              faqs: [
                { q: "বাংলাদেশে কাদের জন্য আয়কর রিটার্ন দাখিল করা বাধ্যতামূলক?", a: "যাদের করযোগ্য আয় সীমার ওপরে (সাধারণত পুরুষদের জন্য ৩.৫ লক্ষ এবং মহিলা ও ৬৫ বছরের ঊর্ধ্ব করদাতাদের জন্য ৪ লক্ষ টাকা), অথবা যাদের ক্রেডিট কার্ড আছে, গাড়ি আছে, সিটি কর্পোরেশনে ব্যবসা বা ট্রেড লাইসেন্স আছে এবং নির্দিষ্ট ৪৩টি সরকারি/বেসরকারি সেবা গ্রহণের জন্য যাদের টিন (TIN) সার্টিফিকেট বাধ্যতামূক করা হয়েছে, তাদের সবার জন্য রিটার্ন দাখিল করা বাধ্যতামূলক।" },
                { q: "প্রতিটি সার্ভিস সম্পন্ন হতে সাধারণত কেমন সময় লাগে?", a: "ই-টিন (e-TIN) বা বিন (BIN) রেজিস্ট্রেশন সাধারণত ২৪ থেকে ৪৮ ঘণ্টার মধ্যে হয়ে যায়। ব্যক্তিগত ট্যাক্স ফাইল ১-৩ কার্যদিবসের মধ্যে এবং কোম্পানির গঠন বা আরজেএসসি (RJSC) ফাইলিং সরকারি অনুমোদন সাপেক্ষে ৭ থেকে ১৪ কার্যদিবস পর্যন্ত সময় লাগতে পারে।" },
                { q: "আপনারা কি প্রাতিষ্ঠানিক কর্পোরেট এবং ট্যাক্স অডিট ক্লায়েন্টদের সেবা দেন?", a: "হ্যাঁ, আমাদের প্যানেলে অভিজ্ঞ চার্টার্ড অ্যাকাউন্ট্যান্ট (CA) এবং ট্যাক্স আইনজীবীরা রয়েছেন। আমরা লিমিটেড কোম্পানিগুলোর বার্ষিক অডিট রিপোর্ট তৈরি, কোম্পানির ট্যাক্স অ্যাসেসমেন্ট এবং এনবিআর-এর অডিট নোটিশের আইনি ডিফেন্স বা শুনানির কাজ নিয়মিত পরিচালনা করি।" },
                { q: "আমাদের গোপনীয় তথ্য এবং ফাইনান্সিয়াল ডাটা কি এখানে নিরাপদ?", a: "শতভাগ নিরাপদ। আপনার জাতীয় পরিচয়পত্র, আয়ের উৎস, ব্যাংক স্টেটমেন্ট এবং কোম্পানির সকল বাণিজ্যিক নথিপত্র আমাদের হাইলি সিকিউরড এনক্রিপ্টেড সার্ভারে সংরক্ষিত থাকে। ক্লায়েন্টের তথ্যের গোপনীয়তা রক্ষা করা আমাদের সর্বোচ্চ অগ্রাধিকার।" },
              ],
            };
            const active = isBn ? bn : c;
            const bulletIcons = active.bullets.map((b) => {
              const { Icon, colorClass } = getServiceBadge(b, b);
              return { Icon, className: `${colorClass} text-white` };
            });
            return <SeoFaqBlock title={active.title} intro={active.intro} bullets={active.bullets} bulletIcons={bulletIcons} faqs={active.faqs} />;
          })()}
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["services"]} />
        </div>
      </section>
      <HiddenSeoContext topic="services" />
    </SiteShell>
  );
}
